import { createContext, useContext, useState, useEffect, useMemo } from "react";
import api from "@/lib/api";
import { computeTax } from "@/lib/tax";

const CartContext = createContext(null);

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try { return JSON.parse(localStorage.getItem("shoply_cart") || "[]").map((i) => ({ lineId: i.lineId || `${i.product.id}::${i.uom || ""}`, uom: i.uom || "", ...i })); } catch { return []; }
  });
  const [discountPct, setDiscountPct] = useState(0);
  const [tip, setTip] = useState(0);
  const [taxConfig, setTaxConfig] = useState(null);
  const [province, setProvince] = useState("");

  useEffect(() => {
    localStorage.setItem("shoply_cart", JSON.stringify(items));
  }, [items]);

  useEffect(() => {
    api.get("/tax/config").then(({ data }) => { setTaxConfig(data); setProvince((p) => p || data.default_province || ""); }).catch(() => {});
  }, []);

  const addItem = (product, uom = "") => {
    const lineId = `${product.id}::${uom || ""}`;
    setItems((prev) => {
      const found = prev.find((i) => i.lineId === lineId);
      if (found) return prev.map((i) => i.lineId === lineId ? { ...i, quantity: i.quantity + 1 } : i);
      return [...prev, { lineId, product, uom, quantity: 1 }];
    });
  };
  const setQty = (lineId, qty) => {
    if (qty <= 0) return removeItem(lineId);
    setItems((prev) => prev.map((i) => i.lineId === lineId ? { ...i, quantity: qty } : i));
  };
  const removeItem = (lineId) => setItems((prev) => prev.filter((i) => i.lineId !== lineId));
  const clear = () => { setItems([]); setDiscountPct(0); setTip(0); };

  const totals = useMemo(() => {
    const subtotal = items.reduce((s, i) => s + i.product.price * i.quantity, 0);
    const discount = subtotal * (discountPct / 100);
    const taxedBase = subtotal - discount;
    const t = computeTax(taxConfig, province, taxedBase);
    const total = taxedBase + t.amount + Number(tip || 0);
    return {
      subtotal: +subtotal.toFixed(2), discount: +discount.toFixed(2),
      tax: +t.amount.toFixed(2), taxBreakdown: t.breakdown, taxPct: t.rate, province,
      total: +total.toFixed(2),
      count: items.reduce((s, i) => s + i.quantity, 0),
    };
  }, [items, discountPct, tip, taxConfig, province]);

  const value = { items, addItem, setQty, removeItem, clear, discountPct, setDiscountPct, tip, setTip, totals, taxConfig, province, setProvince };
  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export const useCart = () => useContext(CartContext);
